import React, { useEffect, useState } from 'react';
import { Instructor } from '../../types/instructores';
import { Nivel } from '../../types/niveles';
import { Evento, EventFormProps } from '../../types/eventos';
import { X, AlertTriangle } from 'lucide-react';
import ConfirmationModal from '../common/ConfirmationModal';
import { useInstructoresQuery, useNivelesQuery } from '../../services';
import { useBranding } from '../../contexts/BrandingContext';
import { idsDeInscritos } from '../../utils/utils';

const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

// 'YYYY-MM-DD' se parte a mano a propósito: new Date('2026-08-24') es medianoche
// UTC y en husos negativos caería en el día anterior, dando mal el día de la semana.
const nombreDiaSemana = (isoDate: string): string => {
    const [anio, mes, dia] = isoDate.split('-').map(Number);
    if (!anio || !mes || !dia) return '';
    return DIAS_SEMANA[new Date(anio, mes - 1, dia).getDay()];
};

const EventForm: React.FC<EventFormProps> = ({ initialDate, initialStartTime, initialEndTime, onSave, onClose, event }) => {
    const { branding } = useBranding();
    const roomOptions = branding?.rooms ?? [];
    const [sala, setSala] = useState('');
    const [fecha, setFecha] = useState(initialDate);
    const [horaInicio, setHoraInicio] = useState(initialStartTime);
    const [horaFin, setHoraFin] = useState(initialEndTime);
    const [nivelId, setNivelId] = useState('');
    const [instructorId, setInstructorId] = useState('');
    const [maxClientes, setMaxClientes] = useState(event?.maxClientes ?? 10);
    const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
    const [selectedNivel, setSelectedNivel] = useState<Nivel | null>(null);

    //variables para el modal de confirmacion antes de modificar el evento
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [formData, setFormData] = useState<Evento | null>(null);

    // Casilla: aplicar el cambio a toda la serie en vez de solo a esta clase.
    // La serie la identifica el backend por el día y la hora ORIGINALES del evento,
    // no por los que se estén escribiendo ahora en el formulario.
    const [aplicarAFuturos, setAplicarAFuturos] = useState(false);

    const fechaOriginal = event?.fecha ? new Date(event.fecha).toISOString().split('T')[0] : '';
    const diaSerie = fechaOriginal ? nombreDiaSemana(fechaOriginal) : '';
    const horaSerie = event?.horaInicio ?? '';

    const { data: niveles = [], isLoading: isLoadingNivel, isError: isErrorNivel } = useNivelesQuery();

    const { data: instructores = [], isLoading: isLoadingInstructor, isError: isErrorInstructor } = useInstructoresQuery();

    useEffect(() => {
        if (event) {
            setNivelId(event.nivel?._id ?? '');
            setInstructorId(event.instructor?._id ?? '');
            setSala(event.sala);
            setFecha(new Date(event.fecha).toISOString().split('T')[0]);
            setHoraInicio(event.horaInicio);
            setHoraFin(event.horaFin);
            setMaxClientes(event.maxClientes ?? 10);
        }
    }, [event, niveles, instructores]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newEvent: Evento = {
            nivel_id: nivelId,
            instructor_id: instructorId,
            sala,
            fecha,
            horaInicio,
            horaFin,
            clientesInscritos: idsDeInscritos(event?.clientesInscritos),
            maxClientes: maxClientes,
            cancelado: event?.cancelado || false,
        };

        if (event?._id) {
            newEvent._id = event._id;
            newEvent.actualizarFuturos = aplicarAFuturos;

            // Siempre se confirma al editar: el alcance ya lo eligió el usuario en la
            // casilla, y antes esto dependía de qué campo se hubiera tocado — cambiar
            // solo la sala guardaba sin avisar y sin propagar.
            setFormData(newEvent);
            setShowConfirmation(true);
            return;
        }

        onSave(newEvent);
    };

    const mensajeConfirmacion = aplicarAFuturos
        ? `<strong>Vas a modificar esta clase y todas las siguientes.</strong><br/><br/>
           El cambio se aplicará a esta clase y a <strong>todos los ${diaSerie} a las ${horaSerie}</strong>
           a partir de esta fecha. Los clientes ya inscritos en cada clase se mantienen.<br/><br/>
           Si has cambiado el nivel, también se actualizarán las plazas de los clientes de ese día y hora.
           <br/><br/><strong>Recuerda:</strong> esto no modifica la plantilla semanal. Si más adelante
           vuelves a generar eventos, se crearán con la configuración antigua — actualiza también la
           plantilla si el cambio es permanente.`
        : `<strong>Vas a modificar solo esta clase.</strong><br/><br/>
           El cambio afectará únicamente a la clase del <strong>${fechaOriginal}</strong>.
           El resto de ${diaSerie}s a las ${horaSerie} se quedan como están.<br/><br/>
           Si querías cambiarlos todos, cancela y marca la casilla
           <em>«Aplicar también a los siguientes ${diaSerie}»</em>.`;

    const resetFields = () => {
        setNivelId('');
        setInstructorId('');
        setSala('');
        setFecha(initialDate);
        setHoraInicio(initialStartTime);
        setHoraFin(initialEndTime);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
            <div className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-md max-h-full overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <h2 className="text-2xl font-bold mb-4">{event ? 'Editar Evento' : 'Crear Evento'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700">Nivel:</label>
                        {isLoadingNivel ? (
                            <p>Cargando niveles...</p>
                        ) : isErrorNivel ? (
                            <p>Error al cargar niveles</p>
                        ) : (
                            <select
                                value={nivelId}
                                onChange={(e) => {
                                    setNivelId(e.target.value);
                                    setSelectedNivel(niveles.find((nivel) => nivel._id === e.target.value) || null);
                                }}
                                className="mt-1 p-2 border rounded w-full"
                            >
                                <option value="">Selecciona un nivel</option>
                                {niveles.map((nivel) => (
                                        <option key={nivel._id} value={nivel._id}>
                                            {nivel.nombre}
                                        </option>
                                    ))}
                            </select>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Instructor:</label>
                        {isLoadingInstructor ? (
                            <p>Cargando instructores...</p>
                        ) : isErrorInstructor ? (
                            <p>Error al cargar instructores</p>
                        ) : (
                            <select
                                value={instructorId}
                                onChange={(e) => {
                                    setInstructorId(e.target.value);
                                    setSelectedInstructor(instructores.find((instructor) => instructor._id === e.target.value) || null);
                                }}
                                className="mt-1 p-2 border rounded w-full"
                            >
                                <option value="">Selecciona un instructor</option>
                                {instructores.map((instructor) => (
                                        <option key={instructor._id} value={instructor._id}>
                                            {instructor.nombre}
                                        </option>
                                    ))}
                            </select>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Sala:</label>
                        <select
                            value={sala}
                            onChange={(e) => {
                                setSala(e.target.value);
                                const selected = roomOptions.find((r) => r.name === e.target.value);
                                if (selected) setMaxClientes(selected.capacity);
                            }}
                            className="mt-1 p-2 border rounded w-full"
                        >
                            <option value="">Selecciona una sala</option>
                            {roomOptions.map((room) => (
                                <option key={room.name} value={room.name}>
                                    {room.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Fecha:</label>
                        <input
                            type="date"
                            value={fecha}
                            onChange={(e) => {
                                setFecha(e.target.value);
                                // console.log('fecha', e.target.value);
                            }}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Hora de Inicio:</label>
                        <input
                            type="time"
                            value={horaInicio}
                            onChange={(e) => setHoraInicio(e.target.value)}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Hora de Fin:</label>
                        <input
                            type="time"
                            value={horaFin}
                            onChange={(e) => setHoraFin(e.target.value)}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Capacidad máxima:</label>
                        <input
                            type="number"
                            min={1}
                            value={maxClientes}
                            onChange={(e) => setMaxClientes(Number(e.target.value))}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    {event?._id && (
                        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={aplicarAFuturos}
                                    onChange={(e) => setAplicarAFuturos(e.target.checked)}
                                    className="mt-1 shrink-0"
                                />
                                <span className="text-sm text-gray-700">
                                    Aplicar también a los siguientes <strong>{diaSerie}</strong> a las{' '}
                                    <strong>{horaSerie}</strong>
                                    <span className="block text-xs text-gray-500 mt-1">
                                        Sin marcar, el cambio afecta solo a esta clase.
                                    </span>
                                </span>
                            </label>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                resetFields();
                                onClose();
                            }}
                            className="mr-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded-md"
                        >
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
            {showConfirmation && (
                <ConfirmationModal
                    isOpen={showConfirmation}
                    onClose={() => setShowConfirmation(false)}
                    onConfirm={() => {
                        if (formData) {
                            onSave(formData);
                            setShowConfirmation(false);
                        }
                    }}
                    confirmMessage={mensajeConfirmacion}
                />
            )}
        </div>
    );
};

export default EventForm;